import { FormikCheckBox, FormikInput, FormikInputGroup, FormikSelect, FormikSlider, Headline } from '@n2-common/ui';
import {
    accommodationStatusCodesSe,
    type AccomodationStatusValue,
    employmentStatusCodeSe,
    martialStatusValues,
    trueFalseValues,
    type EmploymentStatusCode,
} from '@n2-common/uiFieldValues/applicationFormFields';
import { amount } from '@n2-common/validation/common/amount';
import { TFunction } from 'i18next';
import { AmountInput, EmailInput, SwedishSsnInput, SwedishMobilePhoneNumberInput } from '@n2-common/ui/CustomInput';
import { stringRequired } from '@n2-common/validation/common/string';
import { emailRequired } from '@n2-common/validation/common/email';
import { swedishPhoneNumber } from '@n2-common/validation/se/phoneNumber';
import { swedishNationalId } from '@n2-common/validation/se/nationalId';
import { addPleaseSelectOption } from '@n2-common/components/MultiStageOnePageWizard';
import * as Yup from 'yup';
import React from 'react';
import { specifyAccommodationTypeOther, specifyAccomodationCost, specifyEmployerName } from './helpers';
import style from '../style.module.scss';
import { LoanSeApplicationInputModel as LoanSeApplicationInputModelType } from '@n2-common/serverGeneratedTypings/loanSeApplicationInputModel';
import { LoanSeApplicantData as LoanSeApplicantDataType } from '@n2-common/serverGeneratedTypings/loanSeApplicantData';
import type { StageField, WizardStage } from '@n2-common/components/MultiStageOnePageWizard/types';
import { addComparrisonValidation } from '@n2-common/lib/yup-helpers';
import AmountVisualizer, { CliFieldConfig } from '../AmountVisualizer';
import { formatWithCurrencyNoDecimals } from '@n2-common/lib/money';
import { ConsentType } from '@n2-common/uiFieldValues/consents';
import { NumberOfTermsValues } from '../const';

export type SwedishApplicant = Override<
    PropertyNaming<LoanSeApplicantDataType>,
    {
        accommodationType: AccomodationStatusValue | '';
        employmentStatus: EmploymentStatusCode | '';
    }
>;

export type SwedishLoanApplication = Override<
    PropertyNaming<Omit<LoanSeApplicationInputModelType, 'Tracking'>>,
    {
        coApplicant: SwedishApplicant | null;
        mainApplicant: SwedishApplicant;
        gpid: Guid;
        coSignerGpid?: Guid;
        consents: ConsentType;
    }
>;

export type SwedishLoanApplicationCleaned = Override<
    SwedishLoanApplication,
    {
        consents: string[];
    }
>;

export type ApplicantType = 'mainApplicant' | 'coApplicant';

export const initialApplicantValue: SwedishApplicant = {
    numberOfChildren: '' as unknown as number | null, // This is to allow '' as initial value, while autogenerated type don't allow, and React don't allow null!
    email: '',
    phonenumber: '',
    nationalId: '',
    maritalStatus: '',
    employmentStatus: '',
    nameOfEmployer: '',
    monthlyGrossIncome: '',
    accommodationType: '',
    accommodationCost: '',
    accommodationTypeOther: '',
    hasStudentLoan: '',
    studentLoanMontlyCost: '',
    carboatLoan: '',
    hasCar: '',
};

export const initialValues: SwedishLoanApplication = {
    desiredCredit: '',
    desiredNumberOfTerms: '60',
    refinanceDebt: false,
    hasCoApplicant: false,
    coApplicant: initialApplicantValue,
    mainApplicant: initialApplicantValue,
    gAClientRef: '',
    gpid: '',
    coSignerGpid: '',
    consents: {},
};

export const amountBlockCl = (t: TFunction): WizardStage<SwedishLoanApplication> => ({
    legend: '',
    sectionNameForTracking: 'Amount',
    fields: [
        {
            name: 'desiredCredit',
            validation: amount(10000, 500000, t),
            componentProps: {
                label: 'Lånebelopp',
            },
            Component: AmountInput,
        },
        {
            name: 'desiredNumberOfTerms',
            validation: amount(12, 500000, t),
            componentProps: {
                label: 'Önskad återbetalningstid',
            },
            Component: (props) =>
                React.createElement(FormikSlider, {
                    ...props,
                    options: NumberOfTermsValues.map((value) => ({ value, text: `${value / 12}` })),
                }),
        },
        {
            name: 'refinanceDebt',
            validation: Yup.bool(),
            componentProps: {
                label: 'Samla andra lån och krediter',
            },
            Component: FormikCheckBox,
        },
        {
            name: 'hasCoApplicant',
            validation: Yup.bool(),
            componentProps: {
                label: 'Ansök med en medsökande',
            },
            Component: FormikCheckBox,
        },
    ],
});

export const amountBlockCli = (t: TFunction, config?: CliFieldConfig): WizardStage<SwedishLoanApplication> => {
    if (!config) throw new Error('config must be provided for amountBlockCli');
    const minLimit = config.minIncrement;

    return {
        legend: '',
        sectionNameForTracking: 'Amount',
        fields: [
            {
                name: 'desiredCredit',
                validation: amount(minLimit, config.maxIncrement, t),
                componentProps: {
                    label: t<string>('customer-loan:forms.application.desiredCredit', {
                        minLimit: formatWithCurrencyNoDecimals(minLimit, 'SE'),
                        maxLimit: formatWithCurrencyNoDecimals(config.maxIncrement, 'SE'),
                    }),
                },
                Component: config.amountAsSlider
                    ? (props) =>
                          React.createElement(FormikSlider, {
                              ...props,
                              options: { min: minLimit, max: config.maxIncrement },
                          })
                    : AmountInput,
            },
            {
                name: 'desiredCreditView',
                validation: Yup.bool().nullable(),
                componentProps: {
                    label: '',
                },
                Component: (props) =>
                    React.createElement(AmountVisualizer, {
                        country: 'SE',
                        ...config,
                        ...props,
                    }),
            },
        ],
    };
};

export function personalInformation(t: TFunction, isCli: boolean): WizardStage<SwedishLoanApplication> {
    return {
        legend: (values) => `Personuppgifter ${values.hasCoApplicant ? ' huvudsökande' : ''}`,
        sectionNameForTracking: `contact information`,
        fields: [...personalInformationFields(t, 'mainApplicant', isCli)],
    };
}

export const personalInformationCoAplicant = (t: TFunction, isCli: boolean, config?: CliFieldConfig): WizardStage<SwedishLoanApplication> => ({
    legend: `Personuppgifter medsökande`,
    hidden: (values) => !values.hasCoApplicant,
    sectionNameForTracking: 'Personal info coApplicant',
    fields: [...personalInformationFields(t, 'coApplicant', isCli, config?.coApplicantName)],
});

function* personalInformationFields(
    t: TFunction,
    applicant: ApplicantType,
    isCli: boolean,
    name?: string
): Generator<StageField<SwedishLoanApplication>> {
    if (name) {
        yield {
            name: `${applicant}.nationalIdView`,
            validation: Yup.string().nullable(),
            componentProps: {
                label: 'Namn',
            },
            Component: () => Headline({ level: 5, children: name }),
        };
    }
    yield {
        name: `${applicant}.email`,
        validation: emailRequired(t),
        componentProps: {
            label: 'E-postadress',
        },
        Component: EmailInput,
    };
    yield {
        name: `${applicant}.phonenumber`,
        validation: swedishPhoneNumber(t),
        componentProps: {
            label: 'Mobilnummer',
        },
        Component: SwedishMobilePhoneNumberInput,
    };
    if (!isCli) {
        yield {
            name: `${applicant}.nationalId`,
            validation: swedishNationalId(),
            componentProps: {
                label: 'Personnummer',
            },
            Component: SwedishSsnInput,
        };
    }
    yield {
        name: `${applicant}.maritalStatus`,
        validation: stringRequired(t),
        componentProps: {
            label: 'Civilstånd',
        },
        Component: (props) =>
            React.createElement(FormikSelect, {
                ...props,
                options: addPleaseSelectOption(martialStatusValues(t), t),
            }),
    };
    yield {
        name: `${applicant}.numberOfChildren`,
        validation: amount(0, 16, t),
        componentProps: {
            label: 'Antal barn som bor i hushållet',
        },
        Component: AmountInput,
    };
    if (isCli) {
        yield {
            name: `${applicant}View`,
            validation: Yup.boolean()
                .nullable()
                .required(t<string>('validation:consent.required'))
                .oneOf([true], t<string>('validation:consent.required')),
            Component: FormikCheckBox,
            componentProps: { label: t<string>('customer-loan:forms.application.confirmPersonalInformation') },
        };
    }
}

export const secondBlock = (t: TFunction, applicant: ApplicantType): WizardStage<SwedishLoanApplication> => ({
    hidden: applicant === 'coApplicant' ? (values) => !values.hasCoApplicant : undefined,
    legend: `Ekonomisk situation${applicant === 'coApplicant' ? ' medsökande' : ''}`,
    sectionNameForTracking: `Economical information${applicant === 'coApplicant' ? ' coApplicant' : ''}`,
    fields: [
        {
            name: `${applicant}.employmentStatus`,
            validation: stringRequired(t),
            componentProps: {
                label: 'Din arbetssituation',
            },
            Component: (props) =>
                React.createElement(FormikSelect, {
                    //
                    options: addPleaseSelectOption(employmentStatusCodeSe, t),
                    ...props,
                }),
        },
        {
            name: `${applicant}.nameOfEmployer`,
            validation: addComparrisonValidation<number | '', SwedishApplicant['employmentStatus']>(
                Yup.string(),
                t<string>('validation:string.required'),
                `${applicant}.employmentStatus`,
                (val, other) => !specifyEmployerName(other) || !!val
            ),
            componentProps: {
                label: 'Arbetsgivare',
            },
            hidden: (values) => !specifyEmployerName(values[applicant]?.employmentStatus),
            Component: (props) => React.createElement(FormikInput, { ...props, type: 'text' }),
        },
        {
            name: `${applicant}.monthlyGrossIncome`,
            validation: amount(0, Number.MAX_SAFE_INTEGER, t),
            componentProps: {
                label: 'Månadsinkomst (brutto)',
            },
            Component: (props) =>
                React.createElement(AmountInput, {
                    ...props,
                    helptext: 'Din sammanlagda månadsinkomst. Om du har flera inkomster ber vi dig räkna ihop dessa',
                }),
        },
        {
            name: `${applicant}.accommodationType`,
            validation: stringRequired(t),
            componentProps: {
                label: 'Boendeform',
            },
            Component: (props) => React.createElement(FormikSelect, { ...props, options: addPleaseSelectOption(accommodationStatusCodesSe, t) }),
        },
        {
            name: `${applicant}.accommodationTypeOther`,
            validation: addComparrisonValidation<number | '', SwedishApplicant['accommodationType']>(
                Yup.string(),
                t<string>('validation:string.required'),
                `${applicant}.accommodationType`,
                (val, other) => !specifyAccommodationTypeOther(other) || !!val
            ),
            componentProps: {
                label: 'Annat, vad?',
            },
            hidden: (values) => !specifyAccommodationTypeOther(values[applicant]?.accommodationType),
            Component: (props) => React.createElement(FormikInput, { ...props, type: 'text' }),
        },
        {
            name: `${applicant}.accommodationCost`,
            validation: addComparrisonValidation<number | '', SwedishApplicant['accommodationType']>(
                Yup.string(),
                t<string>('validation:string.required'),
                `${applicant}.accommodationType`,
                (val, other) => !specifyAccomodationCost(other) || !!val
            ),
            componentProps: {
                label: 'Hyreskostnad/ månadsavgift',
            },
            hidden: (values) => !specifyAccomodationCost(values[applicant]?.accommodationType),
            Component: AmountInput,
        },
    ],
});

export const thirdBlock = (t: TFunction, applicant: ApplicantType, isCli: boolean): WizardStage<SwedishLoanApplication> => ({
    hidden: applicant === 'coApplicant' ? (values) => !values.hasCoApplicant : undefined,
    legend: `Andra kostnader${applicant === 'coApplicant' ? ' medsökande' : ''}`,
    sectionNameForTracking: `Debt information${applicant === 'coApplicant' ? ' coApplicant' : ''}`,
    fields: [...thirdBlockFields(t, applicant, isCli)],
});

function* thirdBlockFields(t: TFunction, applicant: ApplicantType, isCli: boolean): Generator<StageField<SwedishLoanApplication>> {
    yield {
        name: `${applicant}.hasCar`,
        validation: stringRequired(t),
        componentProps: {
            label: 'Äger eller leasar du en bil?',
        },
        Component: (props) =>
            React.createElement(FormikInputGroup, {
                ...props,
                type: 'radio',
                options: trueFalseValues(t),
                className: style.radioButtons,
                horisontal: true,
            }),
    };
    yield {
        name: `${applicant}.hasStudentLoan`,
        validation: stringRequired(t),
        componentProps: {
            label: 'Har du övriga lån, exklusive bolån?',
            helptext: 'Din del av månadsvisa räntor och avbetalningar för andra lån och borgensåtaganden, exklusive bolån och fordonslån.',
        },
        Component: (props) =>
            React.createElement(FormikInputGroup, {
                ...props,
                type: 'radio',
                options: trueFalseValues(t),
                className: style.radioButtons,
                horisontal: true,
            }),
    };
    yield {
        name: `${applicant}.studentLoanMontlyCost`,
        validation: addComparrisonValidation<number | '', SwedishApplicant['hasStudentLoan']>(
            Yup.string(),
            t<string>('validation:string.required'),
            `${applicant}.hasStudentLoan`,
            (val, other) => (other == 'true' ? !!val : true)
        ),
        componentProps: {
            label: 'Kostnader övriga lån',
        },
        Component: AmountInput,
        hidden: (values) => values[applicant]?.hasStudentLoan != 'true',
    };
    if (isCli) {
        yield {
            name: `${applicant}View`,
            validation: Yup.boolean()
                .nullable()
                .required(t<string>('validation:consent.required'))
                .oneOf([true], t<string>('validation:consent.required')),
            Component: FormikCheckBox,
            componentProps: { label: t<string>('customer-loan:forms.application.confirmFinancialSituation') },
        };
    }
}
